import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { calendarService } from '../services/calendarService';

// Async thunks for calendar operations
export const fetchCalendarEvents = createAsyncThunk(
  'calendar/fetchCalendarEvents',
  async ({ startDate, endDate, filters }, { rejectWithValue }) => {
    try {
      const response = await calendarService.getCalendarEvents(startDate, endDate, filters);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getEventsForDay = createAsyncThunk(
  'calendar/getEventsForDay',
  async ({ date, filters }, { rejectWithValue }) => {
    try {
      const response = await calendarService.getEventsForDay(date, filters);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getEventDetails = createAsyncThunk(
  'calendar/getEventDetails',
  async (eventId, { rejectWithValue }) => {
    try {
      const response = await calendarService.getEventDetails(eventId);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getEventsByType = createAsyncThunk(
  'calendar/getEventsByType',
  async ({ userId, eventType, startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await calendarService.getEventsByType(userId, eventType, startDate, endDate);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createEvent = createAsyncThunk(
  'calendar/createEvent',
  async (eventData, { rejectWithValue }) => {
    try {
      const response = await calendarService.createEvent(eventData);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateEvent = createAsyncThunk(
  'calendar/updateEvent',
  async ({ eventId, updatedData }, { rejectWithValue }) => {
    try {
      const response = await calendarService.updateEvent(eventId, updatedData);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateEventStatus = createAsyncThunk(
  'calendar/updateEventStatus',
  async ({ eventId, updatedStatus }, { rejectWithValue }) => {
    try {
      const response = await calendarService.updateEventStatus(eventId, updatedStatus);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'calendar/deleteEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      const response = await calendarService.deleteEvent(eventId);
      return response.data ? response.data : response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const calendarSlice = createSlice({
  name: 'calendar',
  initialState: {
    events: [],
    selectedEvent: null,
    loading: false,
    error: null,
  },
  reducers: {
    setSelectedEvent: (state, action) => {
      state.selectedEvent = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get calendar events
      .addCase(fetchCalendarEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCalendarEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload.filter(event => event.type === "reminder");
      })
      .addCase(fetchCalendarEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get events for day
      .addCase(getEventsForDay.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEventsForDay.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload.filter(event => event.type === "reminder");
      })
      .addCase(getEventsForDay.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get event details
      .addCase(getEventDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEventDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedEvent = action.payload;
      })
      .addCase(getEventDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get events by type
      .addCase(getEventsByType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEventsByType.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload.filter(event => event.type === "reminder");
      })
      .addCase(getEventsByType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create event
      .addCase(createEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.events.push(action.payload);
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update event
      .addCase(updateEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.loading = false;
        const updatedEvent = action.payload;
        const index = state.events.findIndex(event => event.id === updatedEvent.id);
        if (index !== -1) {
          state.events[index] = updatedEvent;
        }
        if (state.selectedEvent?.id === updatedEvent.id) {
          state.selectedEvent = updatedEvent;
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update event status
      .addCase(updateEventStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEventStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedEvent = action.payload;
        const index = state.events.findIndex(event => event.id === updatedEvent.id);
        if (index !== -1) {
          state.events[index] = updatedEvent;
        }
        if (state.selectedEvent?.id === updatedEvent.id) {
          state.selectedEvent = updatedEvent;
        }
      })
      .addCase(updateEventStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete event
      .addCase(deleteEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.loading = false;
        const eventId = action.payload;
        state.events = state.events.filter(event => event.id !== eventId);
        if (state.selectedEvent?.id === eventId) {
          state.selectedEvent = null;
        }
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setSelectedEvent, clearError } = calendarSlice.actions;
export default calendarSlice.reducer;